import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IPerformanceLimitsConfig,
  IPerformanceLimitsTabsConfig,
} from '@shared/interfaces/performance-limits.interface';
import {
  ConvertLimitsData,
  ConvertLimitsGender,
  ConvertLimitsLevel,
} from '@shared/interfaces/convert-limits.interface';
import { SportService } from '@core/services/sports/sport.service';
import { HelperService } from '@shared/services/helper.service';
import { AppConstants } from '@core/constants';
import { buildTabData, RawTabData } from './performance-limits-tab-builder';
import { withDelayedLoadingIndicator } from '@shared/helpers/delayed-loading.helper';
import { ICustomAthleteCategory } from '@shared/interfaces/sport-configuration-form-data.interface';

export interface PerformanceLimitTab {
  key: string;
  id?: number;
  name: string;
  isCustom: boolean;
  isConverting: boolean;
  form: FormGroup;
}

type TabFieldKey = 'male_upper' | 'male_lower' | 'female_upper' | 'female_lower';

interface TabFieldSpec {
  key: TabFieldKey;
  gender: ConvertLimitsGender;
}

const TAB_FIELDS: TabFieldSpec[] = [
  { key: 'male_upper', gender: 'male' },
  { key: 'male_lower', gender: 'male' },
  { key: 'female_upper', gender: 'female' },
  { key: 'female_lower', gender: 'female' },
];

const ValidateFields = ['male_upper', 'male_lower', 'male_stdDev', 'female_upper', 'female_lower', 'female_stdDev'];

@Component({
  selector: 'app-performance-limits',
  templateUrl: './performance-limits.component.html',
  styleUrls: ['./performance-limits.component.scss'],
})
export class PerformanceLimitsComponent implements OnChanges {
  private fb: FormBuilder = inject(FormBuilder);
  private sportService: SportService = inject(SportService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  @Input() public performanceLimitsByDataPoint: Record<string, IPerformanceLimitsConfig>;
  @Input() public dataPointKey: string;
  @Input() public sportId: number;
  @Input() public referenceSystemId: number | undefined;
  @Input() public customAthleteCategoryList: ICustomAthleteCategory[] = [];
  @Input() public angleName?: string;
  @Input() public dataPointNumber: number;
  @Input() public anglePositionNumber: number;
  @Input() public isApplyOverlay: boolean;
  @Output() public performanceLimitChanged = new EventEmitter<IPerformanceLimitsConfig>();

  public tabs: PerformanceLimitTab[] = [];
  public config: IPerformanceLimitsConfig;

  private hiddenCustomEntries: Record<string, IPerformanceLimitsTabsConfig> = {};
  private inputChanged$ = new Subject<void>();

  constructor() {
    this.inputChanged$
      .pipe(
        switchMap(() =>
          this.rebuildTabs().pipe(
            catchError((err) => {
              console.error('Failed to build performance limit tabs', err);
              return of(undefined);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.subscribeToTabValueChanges());
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.config = this.performanceLimitsByDataPoint[this.dataPointKey];
    this.inputChanged$.next();
  }

  public trackByTabKey(_index: number, tab: PerformanceLimitTab): string {
    return tab.key;
  }

  public isTextInput(): boolean {
    return !!this.referenceSystemId && [6, 7, 8, 9].includes(this.referenceSystemId);
  }

  private rebuildTabs(): Observable<void> {
    const { visible, hiddenCustomEntries } = buildTabData(this.config, this.customAthleteCategoryList);
    this.hiddenCustomEntries = hiddenCustomEntries;
    this.tabs = visible.map((raw) => this.toPerformanceLimitTab(raw));

    const conversions = this.tabs.map((tab) => this.convertAndPatchTab(tab));
    return forkJoin(conversions.length ? conversions : [of(undefined)]).pipe(map(() => undefined));
  }

  public onCustomTabValueChanged(tab: PerformanceLimitTab): void {
    tab.form.controls['is_default_value'].setValue(false);
    this.validateForm(tab.form);
    this.setStandardDeviation(tab.form);
  }

  public setStandardDeviation(form: FormGroup | undefined): void {
    if (form && this.referenceSystemId && [6, 7, 8, 9].includes(this.referenceSystemId)) {
      const upperMale = this.paceToSeconds(form?.get('male_upper')?.value || '1:00');
      const lowerMale = this.paceToSeconds(form?.get('male_lower')?.value || '1:00');
      const upperFemale = this.paceToSeconds(form?.get('female_upper')?.value || '1:00');
      const lowerFemale = this.paceToSeconds(form?.get('female_lower')?.value || '1:00');

      const maleStdDev: any = form.get('male_stdDev');
      const femaleStdDev: any = form.get('female_stdDev');

      const stdDevMale: number = Math.abs(upperMale - lowerMale) / 6;
      const stdDevFemale: number = Math.abs(upperFemale - lowerFemale) / 6;

      maleStdDev.setValue(stdDevMale.toFixed(2));
      femaleStdDev.setValue(stdDevFemale.toFixed(2));
      return;
    }

    if (form) {
      const upperMale: number = form.get('male_upper')?.value || 1;
      const lowerMale: number = form.get('male_lower')?.value || 1;
      const upperFemale: number = form.get('female_upper')?.value || 1;
      const lowerFemale: number = form.get('female_lower')?.value || 1;
      const maleStdDev: any = form.get('male_stdDev');
      const femaleStdDev: any = form.get('female_stdDev');

      const stdDevMale: number = Math.abs(upperMale - lowerMale) / 6;
      const stdDevFemale: number = Math.abs(upperFemale - lowerFemale) / 6;

      maleStdDev.setValue(stdDevMale.toFixed(2));
      femaleStdDev.setValue(stdDevFemale.toFixed(2));
    }
  }

  private isAboveZero(value: number | string): boolean {
    if (typeof value === 'string') {
      return value[0] !== '-';
    }
    return value >= 0;
  }

  private isValidPace(value: string | number): boolean {
    if (typeof value === 'number') {
      return true;
    }

    const pacePattern = /^(\d+):(\d{2})(\.\d)?$/;
    const match = value.match(pacePattern);

    if (!match) {
      return false;
    }

    return parseInt(match[2], 10) < 60;
  }

  private validateForm(formGroup: FormGroup<any>): void {
    const formValues = formGroup.value;
    for (const fieldName of ValidateFields) {
      if (!this.isAboveZero(formValues[fieldName])) {
        formGroup.controls[fieldName]?.setErrors({ min: true });
      }
      if (!this.isValidPace(formValues[fieldName])) {
        formGroup.controls[fieldName]?.setErrors({ invalid: true });
      }
    }
  }

  private toPerformanceLimitTab(raw: RawTabData): PerformanceLimitTab {
    return {
      key: raw.key,
      id: raw.id,
      name: raw.name,
      isCustom: raw.isCustom,
      isConverting: false,
      form: this.buildFormForValues(raw.values),
    };
  }

  private buildFormForValues(values: IPerformanceLimitsTabsConfig): FormGroup {
    return this.fb.group({
      male_lower: [values.male_lower ?? 1],
      male_upper: [values.male_upper ?? 1],
      male_stdDev: [values.male_stdDev ?? 1],
      female_lower: [values.female_lower ?? 1],
      female_upper: [values.female_upper ?? 1],
      female_stdDev: [values.female_stdDev ?? 1],
      is_default_value: [values.is_default_value ?? false],
    });
  }

  private convertAndPatchTab(tab: PerformanceLimitTab): Observable<void> {
    const level: ConvertLimitsLevel = tab.isCustom ? 'amateur' : (tab.key as ConvertLimitsLevel);
    const conversion$ = this.convertTabsToReferenceSystem(tab.form.value, level);

    return withDelayedLoadingIndicator(
      conversion$,
      (isLoading) => (tab.isConverting = isLoading),
      AppConstants.CONVERSION_LOADING_DELAY_MS
    ).pipe(
      map((converted) => {
        tab.form.patchValue(converted);
      })
    );
  }

  private subscribeToTabValueChanges(): void {
    this.tabs
      .filter((tab) => tab.isCustom)
      .forEach((tab) => {
        tab.form.valueChanges.pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.emitAllFormsData();
        });
      });
  }

  private emitAllFormsData(): void {
    const customTabs = this.tabs.filter((tab) => tab.isCustom);

    if (!customTabs.every((tab) => tab.form.valid)) {
      return;
    }

    const conversions: Record<string, Observable<IPerformanceLimitsTabsConfig>> = Object.fromEntries(
      customTabs.map((tab) => [tab.key, this.convertReferenceSystemToMS(tab.form.value, 'amateur')])
    );

    forkJoin(conversions)
      .pipe(
        catchError((err) => {
          console.error('Failed to convert custom tabs for save', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((convertedByKey) => {
        const custom_tab_object = this.buildCustomTabObjectForEmit(customTabs, convertedByKey);
        this.performanceLimitChanged.emit({
          tabs: {
            pro: this.getTabForm('pro').value,
            amateur: this.getTabForm('amateur').value,
            recreational: this.getTabForm('recreational').value,
          },
          custom_tab_object,
        });
      });
  }

  private buildCustomTabObjectForEmit(
    customTabs: PerformanceLimitTab[],
    convertedByKey: Record<string, IPerformanceLimitsTabsConfig>
  ): Record<string, IPerformanceLimitsTabsConfig> {
    const activeEntries = Object.fromEntries(
      customTabs.map((tab) => [tab.name, { ...convertedByKey[tab.key], id: tab.id, name: tab.name }])
    );

    return { ...this.hiddenCustomEntries, ...activeEntries };
  }

  private getTabForm(key: string): FormGroup {
    return this.tabs.find((tab) => tab.key === key)!.form;
  }

  // --- conversion functions (unchanged from earlier work) ---

  public convertTabsToReferenceSystem(
    tab: IPerformanceLimitsTabsConfig,
    level: ConvertLimitsLevel
  ): Observable<IPerformanceLimitsTabsConfig> {
    const isPaceBased = this.isTextInput();
    const defaultValue: number | string = isPaceBased ? '1:00' : 1;

    if (this.referenceSystemId !== 1 && this.referenceSystemId !== 2) {
      return this.convertTabsToReferenceSystemSync(tab, level, isPaceBased, defaultValue);
    }

    const presentFields = TAB_FIELDS.filter((f) => tab[f.key] != null);
    const batchItems: ConvertLimitsData[] = presentFields.map((f) => ({
      speed_value: tab[f.key] as number,
      is_absolute: this.referenceSystemId === 1,
      gender: f.gender,
      level,
    }));

    return this.sportService.batchConvertLimits(this.sportId!, batchItems).pipe(
      map((results) => {
        const valuesByKey = Object.fromEntries(
          presentFields.map((f, i) => [f.key, results[i]?.power_value ?? 0])
        ) as Record<TabFieldKey, number>;

        const {
          male_upper: maleUpper,
          male_lower: maleLower,
          female_upper: femaleUpper,
          female_lower: femaleLower,
        } = valuesByKey;

        return {
          ...tab,
          male_stdDev:
            maleUpper != null && maleLower != null ? (Math.abs(maleUpper - maleLower) / 6).toFixed(2) : defaultValue,
          female_stdDev:
            femaleUpper != null && femaleLower != null
              ? (Math.abs(femaleUpper - femaleLower) / 6).toFixed(2)
              : defaultValue,
          male_upper: maleUpper ?? defaultValue,
          male_lower: maleLower ?? defaultValue,
          female_upper: femaleUpper ?? defaultValue,
          female_lower: femaleLower ?? defaultValue,
        };
      })
    );
  }

  private convertTabsToReferenceSystemSync(
    tab: IPerformanceLimitsTabsConfig,
    level: ConvertLimitsLevel,
    isPaceBased: boolean,
    defaultValue: number | string
  ): Observable<IPerformanceLimitsTabsConfig> {
    const hasMaleUpper = tab.male_upper != null;
    const hasMaleLower = tab.male_lower != null;
    const hasFemaleUpper = tab.female_upper != null;
    const hasFemaleLower = tab.female_lower != null;

    return forkJoin({
      maleUpper: hasMaleUpper ? this.convertFromMS(tab.male_upper as number, level, 'male', true) : of(null),
      maleLower: hasMaleLower ? this.convertFromMS(tab.male_lower as number, level, 'male', true) : of(null),
      femaleUpper: hasFemaleUpper ? this.convertFromMS(tab.female_upper as number, level, 'female', true) : of(null),
      femaleLower: hasFemaleLower ? this.convertFromMS(tab.female_lower as number, level, 'female', true) : of(null),
    }).pipe(
      map(({ maleUpper, maleLower, femaleUpper, femaleLower }) => ({
        ...tab,
        male_stdDev:
          maleUpper != null && maleLower != null
            ? (Math.abs((maleUpper as number) - (maleLower as number)) / 6).toFixed(2)
            : defaultValue,
        female_stdDev:
          femaleUpper != null && femaleLower != null
            ? (Math.abs((femaleUpper as number) - (femaleLower as number)) / 6).toFixed(2)
            : defaultValue,
        male_upper: hasMaleUpper ? this.formatConvertedValue(maleUpper as number, isPaceBased) : defaultValue,
        male_lower: hasMaleLower ? this.formatConvertedValue(maleLower as number, isPaceBased) : defaultValue,
        female_upper: hasFemaleUpper ? this.formatConvertedValue(femaleUpper as number, isPaceBased) : defaultValue,
        female_lower: hasFemaleLower ? this.formatConvertedValue(femaleLower as number, isPaceBased) : defaultValue,
      }))
    );
  }

  private formatConvertedValue(secondsValue: number, isPaceBased: boolean): number | string {
    return isPaceBased ? this.timeToMinSec(secondsValue) : secondsValue;
  }

  public convertReferenceSystemToMS(
    tab: IPerformanceLimitsTabsConfig,
    level: ConvertLimitsLevel
  ): Observable<IPerformanceLimitsTabsConfig> {
    if (this.referenceSystemId !== 1 && this.referenceSystemId !== 2) {
      return this.convertReferenceSystemToMSSync(tab, level);
    }

    const batchItems: ConvertLimitsData[] = TAB_FIELDS.map((f) => ({
      power_value: this.toNumericPowerValue(tab[f.key]),
      is_absolute: this.referenceSystemId === 1,
      gender: f.gender,
      level,
    }));

    return this.sportService.batchConvertLimits(this.sportId!, batchItems).pipe(
      map((results) => {
        const [maleUpper, maleLower, femaleUpper, femaleLower] = TAB_FIELDS.map((_, i) =>
          this.valueRounding(results[i]?.speed_value ?? 0)
        );

        tab.male_upper = maleUpper;
        tab.male_lower = maleLower;
        tab.female_upper = femaleUpper;
        tab.female_lower = femaleLower;
        tab.male_stdDev = Math.abs(maleUpper - maleLower) / 6;
        tab.female_stdDev = Math.abs(femaleUpper - femaleLower) / 6;
        return tab;
      })
    );
  }

  private convertReferenceSystemToMSSync(
    tab: IPerformanceLimitsTabsConfig,
    level: ConvertLimitsLevel
  ): Observable<IPerformanceLimitsTabsConfig> {
    return forkJoin({
      male_lower: this.convertToMs(tab.male_lower as number, level, 'male'),
      male_upper: this.convertToMs(tab.male_upper as number, level, 'male'),
      female_lower: this.convertToMs(tab.female_lower as number, level, 'female'),
      female_upper: this.convertToMs(tab.female_upper as number, level, 'female'),
    }).pipe(
      map(({ male_lower, male_upper, female_lower, female_upper }) => {
        tab.male_lower = male_lower;
        tab.male_upper = male_upper;
        tab.female_lower = female_lower;
        tab.female_upper = female_upper;
        tab.male_stdDev = Math.abs(male_upper - male_lower) / 6;
        tab.female_stdDev = Math.abs(female_upper - female_lower) / 6;
        return tab;
      })
    );
  }

  private toNumericPowerValue(value: string | number): number {
    return typeof value === 'number' ? value : parseFloat(value);
  }

  public convertFromMS(
    value: number,
    level: ConvertLimitsLevel,
    gender: ConvertLimitsGender,
    returnSeconds = false
  ): Observable<number | string> {
    if (!this.referenceSystemId) {
      return of(value);
    }

    // m/s
    if (this.referenceSystemId === 3) {
      return of(value);
    }

    // Power Watts
    if (this.referenceSystemId === 1) {
      return this.msToPower(value, true, gender, level);
    }

    // Power Watts/Kg
    if (this.referenceSystemId === 2) {
      return this.msToPower(value, false, gender, level);
    }

    switch (this.referenceSystemId) {
      case 4:
        // Km/h
        return of(value * 3.6);
      case 5:
        // mph
        return of((value * 3.6) / 1.61);
      case 6: {
        // min:sec/km
        const timeInSeconds = 1000 / value;
        return of(returnSeconds ? timeInSeconds : this.timeToMinSec(timeInSeconds));
      }
      case 7: {
        // min:sec/mile
        const timeInSeconds = (1000 * 1.61) / value;
        return of(returnSeconds ? timeInSeconds : this.timeToMinSec(timeInSeconds));
      }
      case 8: {
        // min:sec/100m
        const timeInSeconds = 100 / value;
        return of(returnSeconds ? timeInSeconds : this.timeToMinSec(timeInSeconds));
      }
      case 9: {
        // min:sec/500m
        const timeInSeconds = 500 / value;
        return of(returnSeconds ? timeInSeconds : this.timeToMinSec(timeInSeconds));
      }
      default:
        return of(value);
    }
  }

  public convertToMs(
    value: string | number,
    level: ConvertLimitsLevel,
    gender: ConvertLimitsGender
  ): Observable<number> {
    if (!this.referenceSystemId) {
      return of(typeof value === 'number' ? value : this.valueRounding(parseFloat(value)));
    }

    // m/s
    if (this.referenceSystemId === 3) {
      return of(typeof value === 'number' ? value : this.valueRounding(parseFloat(value)));
    }

    // Absolute power
    if (this.referenceSystemId === 1) {
      value = typeof value === 'number' ? value : parseFloat(value);
      return this.powerToMs(value, true, gender, level).pipe(map((v) => this.valueRounding(v)));
    }

    // Relative power
    if (this.referenceSystemId === 2) {
      value = typeof value === 'number' ? value : parseFloat(value);
      return this.powerToMs(value).pipe(map((v) => this.valueRounding(v)));
    }

    switch (this.referenceSystemId) {
      case 4:
        return of(this.valueRounding((value as number) / 3.6));
      case 5:
        return of(this.valueRounding(((value as number) * 1.61) / 3.6));
      case 6: {
        if (typeof value === 'number') {
          return of(this.valueRounding(value));
        }
        const timeInSeconds = this.paceToSeconds(value as string);
        return of(this.valueRounding(1000 / timeInSeconds));
      }
      case 7: {
        if (typeof value === 'number') {
          return of(this.valueRounding(value));
        }
        const timeInSeconds = this.paceToSeconds(value as string);
        return of(this.valueRounding((1000 * 1.61) / timeInSeconds));
      }
      case 8: {
        if (typeof value === 'number') {
          return of(this.valueRounding(value));
        }
        const timeInSeconds = this.paceToSeconds(value as string);
        return of(this.valueRounding(100 / timeInSeconds));
      }
      case 9: {
        if (typeof value === 'number') {
          return of(this.valueRounding(value));
        }
        const timeInSeconds = this.paceToSeconds(value as string);
        return of(this.valueRounding(500 / timeInSeconds));
      }
      default:
        return of(this.valueRounding(typeof value === 'number' ? value : parseFloat(value)));
    }
  }

  public powerToMs(
    power_value: number,
    is_absolute = false,
    gender?: ConvertLimitsGender,
    level?: ConvertLimitsLevel
  ): Observable<number> {
    const params: ConvertLimitsData = {
      power_value,
      is_absolute,
      gender,
      level,
    };
    return this.sportService.convertLimits(this.sportId!, params).pipe(map((response) => response.speed_value ?? 0));
  }

  public msToPower(
    speed_value: number,
    is_absolute = false,
    gender?: ConvertLimitsGender,
    level?: ConvertLimitsLevel
  ): Observable<number> {
    const params: ConvertLimitsData = {
      speed_value,
      gender,
      level,
      is_absolute,
    };
    return this.sportService.convertLimits(this.sportId!, params).pipe(map((response) => response.power_value ?? 0));
  }

  public timeToMinSec(timeInSeconds: number): string {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds - minutes * 60;
    let secondsString = seconds.toFixed(1);
    if (seconds < 10) {
      secondsString = `0${secondsString}`;
    }
    return `${minutes}:${secondsString}`;
  }

  public paceToSeconds(paceValue: string): number {
    const [minutes, seconds] = paceValue.split(':');
    return parseFloat(minutes) * 60 + parseFloat(seconds);
  }

  private valueRounding(value: number, decimals = 3): number {
    return Number(value.toFixed(decimals));
  }

  // convertFromMS, convertToMs, powerToMs, msToPower, timeToMinSec, paceToSeconds — all unchanged
}
